import React, { useMemo, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  HelpCircle,
  CheckCircle,
  XCircle,
  Sparkles,
  Loader2
} from 'lucide-react';

// Field configuration based on categories
const FIELD_CONFIGS = {
  material: {
    default: ['硬质合金', '高速钢', 'PCD']
  },
  coating: {
    default: ['无涂层', '普通涂层', '金刚石涂层']
  },
  unit: {
    刀具: ['支', '套', '包'],
    机械部件: ['个', '套', '组', '台'],
    电子产品: ['个', '片', '包', '盒'],
    原材料: ['公斤', '米', '卷', '张', '块'],
    default: ['个', '支', '套', '包', '公斤']
  }
};

const SmartField = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  required = false,
  category = '',
  dependencies = [],
  options = [],
  validation = null,
  helpText = '',
  placeholder = '',
  multiline = false,
  rows = 1,
  disabled = false,
  autoSuggest = false,
  unit = '',
  className = '',
  error = false,
  loading = false,
  ...props
}) => {

  // Get smart options - memoized to prevent recalculation
  const smartOptions = useMemo(() => {
    if (options.length > 0) return options;

    const fieldConfig = FIELD_CONFIGS[name];
    if (!fieldConfig) return [];

    return fieldConfig[category] || fieldConfig.default || [];
  }, [name, category, options]);

  // Validation logic - simplified and inline
  const getValidationState = useCallback(() => {
    if (!validation || !value || value === '') {
      return { error: '', state: 'neutral' };
    }

    try {
      const result = validation(value);
      if (result === true) {
        return { error: '', state: 'success' };
      } else if (typeof result === 'string') {
        return { error: result, state: 'error' };
      } else {
        return { error: '', state: 'neutral' };
      }
    } catch (err) {
      return { error: 'Validation error', state: 'error' };
    }
  }, [validation, value]);

  const validationResult = getValidationState();
  const currentError = validationResult.error;
  const validationState = validationResult.state;

  // Stable change handler
  const handleChange = useCallback((newValue) => {
    if (onChange) {
      onChange(name, newValue);
    }
  }, [onChange, name]);

  // Stable blur handler
  const handleBlur = useCallback((e) => {
    if (onBlur) {
      const blurValue = e.target.value;
      onBlur(blurValue);
    }
  }, [onBlur]);

  // Validation icon
  const getValidationIcon = () => {
    if (loading) {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }

    switch (validationState) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  // Simple suggestions for auto-suggest
  const suggestions = useMemo(() => {
    if (!autoSuggest || !value || value.length <= 1) return [];

    return smartOptions.filter(option =>
      option.toLowerCase().includes(value.toLowerCase()) &&
      option.toLowerCase() !== value.toLowerCase()
    ).slice(0, 3);
  }, [autoSuggest, value, smartOptions]);

  // Determine final error state
  const finalError = error || !!currentError;

  // Helper text color
  const getHelperTextColor = () => {
    if (finalError) return 'text-destructive';
    if (validationState === 'success') return 'text-success';
    return 'text-muted-foreground';
  };

  // Render different field types
  switch (type) {
    case 'select':
      return (
        <div className={`space-y-2 ${className}`}>
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Select
            value={value || ''}
            onValueChange={handleChange}
            disabled={disabled || loading}
          >
            <SelectTrigger className={finalError ? 'border-red-500' : ''}>
              <SelectValue placeholder={`请选择${label}`}>
                {(() => {
                  const selectedOption = smartOptions.find(opt => 
                    (typeof opt === 'object' ? opt.value : opt) === value
                  );
                  return selectedOption 
                    ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption)
                    : value;
                })()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {smartOptions.map((option) => {
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : option;
                return (
                  <SelectItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {(currentError || helpText) && (
            <div className="flex items-center gap-1">
              {getValidationIcon()}
              <span className={`text-xs ${getHelperTextColor()}`}>
                {currentError || helpText}
              </span>
              {helpText && !currentError && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>{helpText}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      );

    case 'autocomplete':
      return (
        <div className={`space-y-2 ${className}`}>
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <div className="relative">
            <Input
              id={name}
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled || loading}
              className={finalError ? 'border-red-500 pr-10' : 'pr-10'}
              list={`${name}-datalist`}
            />
            <datalist id={`${name}-datalist`}>
              {smartOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {getValidationIcon()}
              {helpText && !currentError && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>{helpText}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          {(currentError || helpText) && (
            <span className={`text-xs ${getHelperTextColor()}`}>
              {currentError || helpText}
            </span>
          )}
        </div>
      );

    case 'number':
      return (
        <div className={`space-y-2 ${className}`}>
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <div className="relative">
            <Input
              id={name}
              type="number"
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled || loading}
              className={`${finalError ? 'border-red-500' : ''} ${unit ? 'pr-20' : 'pr-10'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {unit && (
                <span className="text-sm text-muted-foreground">{unit}</span>
              )}
              {getValidationIcon()}
              {helpText && !currentError && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>{helpText}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          {(currentError || helpText) && (
            <span className={`text-xs ${getHelperTextColor()}`}>
              {currentError || helpText}
            </span>
          )}
        </div>
      );

    default:
      return (
        <div className={`space-y-2 ${className}`}>
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {multiline ? (
            <textarea
              id={name}
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled || loading}
              rows={rows}
              className={`flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                finalError ? 'border-red-500' : ''
              }`}
            />
          ) : (
            <div className="relative">
              <Input
                id={name}
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled || loading}
                className={`${finalError ? 'border-red-500' : ''} pr-10`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {autoSuggest && suggestions.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Sparkles className="h-4 w-4 text-primary" />
                      </TooltipTrigger>
                      <TooltipContent>建议: {suggestions.join(', ')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {getValidationIcon()}
                {helpText && !currentError && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>{helpText}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          )}
          {(currentError || helpText) && (
            <span className={`text-xs ${getHelperTextColor()}`}>
              {currentError || helpText}
            </span>
          )}
        </div>
      );
  }
};

export default SmartField;
